// Vendor imports
const Bluebird = require('bluebird')
// Shared imports
const { ExtendableError } = require('shared/error-utils')
// Client lib imports
const airbrake = require('client/lib/airbrake')

// Constants

const DEFAULT_TIMEOUT = 100 // milliseconds

// Error types

class MixExtensionNotInstalledError extends ExtendableError {
  constructor() {
    super('Mix Extension is not installed')
    this.name = 'MixExtensionNotInstalledError' // Preserve name after minifying
  }
}

class MixExtensionCallTimeoutError extends ExtendableError {
  constructor() {
    super('Mix Extension call timed out')
    this.name = 'MixExtensionCallTimeoutError' // Preserve name after minifying
  }
}

// Inspectors

/**
 * @returns {boolean}
 */
function isInstalled() {
  // `mixext` is in the global scope when the extension is installed.
  return typeof window === 'object' && Boolean(window.mixext)
}

/**
 * @returns {HTMLElement|void}
 * @see https://github.com/mix/mix-ext/blob/HEAD/module/iframe/bar.js
 */
function getElement() {
  if (isInstalled()) {
    return document.getElementById('mix-bar')
  }
}

/**
 * @returns {any}
 * @throws {MixExtensionNotInstalledError} if the extension is not installed
 * @see https://github.com/mix/mix-ext/blob/HEAD/module/ext/integration.js
 */
function getReference() {
  if (!isInstalled()) {
    throw new MixExtensionNotInstalledError()
  }
  return window.mixext
}

// Call wrappers

/**
 * @param {string} urlId
 * @returns {Promise.<boolean>}
 */
async function clearUrlCache(urlId) {
  return call('clearUrlCache', [ urlId ], { notifyOnTimeout: false })
  .catch(ignoreError)
}

/**
 * @param {string} urlId
 * @returns {Promise.<boolean>}
 */
async function clearUrlStateCache(urlId) {
  return call('clearUrlStateCache', [ urlId ], { notifyOnTimeout: false })
  .catch(ignoreError)
}

/**
 * @returns {Promise.<boolean>}
 */
async function clearUserCache() {
  return call('clearUserCache', [], { notifyOnTimeout: false })
  .catch(ignoreError)
}

/**
 * @returns {Promise.<boolean>}
 */
async function clearUserStateCache() {
  return call('clearUserStateCache', [], { notifyOnTimeout: false })
  .catch(ignoreError)
}

/**
 * @param {string} domain
 * @returns {Promise.<boolean>}
 */
async function filterDomain(domain) {
  return call('filterDomain', [ domain ])
  .catch(ignoreError)
}

/**
 * Test if the mix-ext integration is working correctly.
 * @returns {Promise.<string>} `pong`
 */
async function ping() {
  return call('ping')
}

/**
 * @param {Object} article
 * @param {Object} [options]
 * @returns {Promise.<boolean>}
 */
async function showUrl(article, options = {}) {
  /**
   * Wait a little longer before timing-out because opening a tab
   * happens in a background thread and sometimes takes a while
   * @see https://app.asana.com/0/102902769366319/1135833214080065/f
   */
  return call('showUrl', [ article, options ], { timeout: 1500 })
}

// Utility functions

/**
 * @param {string} methodName
 * @param {Array.<any>} [methodArgs]
 * @param {Object} [callOptions]
 * @param {boolean} [callOptions.notifyOnTimeout]
 * @param {number} [callOptions.timeout] in milliseconds
 * @returns {Promise.<any>}
 * @see https://github.com/mix/mix-ext/blob/HEAD/module/ext/integration.js
 */
async function call(methodName, methodArgs = [], callOptions = {}) {
  const {
    notifyOnTimeout = true,
    timeout = DEFAULT_TIMEOUT
  } = callOptions
  const ext = getReference()
  const timeoutErr = new MixExtensionCallTimeoutError()
  try {
    let promise = Bluebird.resolve(ext[methodName](...methodArgs))
    if (timeout) {
      promise = promise.timeout(timeout, timeoutErr)
    }
    return await promise
  } catch (err) {
    if (err !== timeoutErr || notifyOnTimeout) {
      airbrake.notify({
        error: err,
        context: { component: 'mix-ext' },
        params: { methodName, methodArgs }
      })
    }
    throw err
  }
}

function ignoreError() { /** Do nothing */ }

// Exports

module.exports = {
  // Error types
  MixExtensionCallTimeoutError,
  MixExtensionNotInstalledError,
  // Inspectors
  getElement,
  getReference,
  isInstalled,
  // Call wrappers
  clearUrlCache,
  clearUrlStateCache,
  clearUserCache,
  clearUserStateCache,
  filterDomain,
  ping,
  showUrl
}
